import type { PublicMailIdentity } from '@embertime/shared'
import { useQuery } from '@tanstack/react-query'
import { Check, Globe, Info, Plug, Plus } from 'lucide-react'
import { api } from '../../lib/api'
import { cn } from '../../lib/utils'

// Section „Absender“ of a mail account card (#839, Mockup „Liste“): each
// identity with type icon, name or SMTP server, „Standard“ and a status badge.

export type IdentityBadgeTone = 'success' | 'warning' | 'danger' | 'muted'

export function identitiesQueryKey(accountId: string) {
  return ['mail-identities', accountId] as const
}

/** Number of ok DNS records (0–3); null while never checked. */
export function dnsOkCount(
  identity: Pick<PublicMailIdentity, 'dnsSpf' | 'dnsDkim' | 'dnsDmarc'>,
): number | null {
  const all = [identity.dnsSpf, identity.dnsDkim, identity.dnsDmarc]
  if (all.every((s) => s === null)) return null
  return all.filter((s) => s === 'ok').length
}

/** Status badge of a row: DNS state for an own domain, test state for SMTP. */
export function identityStatusBadge(identity: PublicMailIdentity): {
  label: string
  tone: IdentityBadgeTone
  ok: boolean
} {
  if (identity.status === 'disabled') return { label: 'deaktiviert', tone: 'muted', ok: false }
  if (identity.kind === 'own_domain') {
    const count = dnsOkCount(identity)
    if (count === null) return { label: 'DNS ?', tone: 'muted', ok: false }
    if (count === 3) return { label: 'DNS ok', tone: 'success', ok: true }
    return { label: `DNS ${count}/3`, tone: 'warning', ok: false }
  }
  if (identity.testOk === true) return { label: 'getestet', tone: 'success', ok: true }
  if (identity.testOk === false) return { label: 'Test fehlgeschlagen', tone: 'danger', ok: false }
  return { label: 'nicht getestet', tone: 'muted', ok: false }
}

/** Second line of a row: the display name, or the SMTP server it goes through. */
export function identitySubtitle(identity: PublicMailIdentity): string {
  if (identity.kind === 'external_smtp') return `über ${identity.smtpHost ?? '–'}`
  return identity.displayName ?? ''
}

const BADGE_TONES: Record<IdentityBadgeTone, string> = {
  success: 'bg-success/15 text-success',
  warning: 'bg-warning/15 text-warning',
  danger: 'bg-danger/15 text-danger',
  muted: 'bg-bg-overlay text-text-tertiary',
}

export function IdentityKindIcon({
  kind,
  size = 'md',
}: {
  kind: PublicMailIdentity['kind']
  size?: 'sm' | 'md'
}) {
  const Icon = kind === 'own_domain' ? Globe : Plug
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-[10px]',
        size === 'md' ? 'h-9 w-9' : 'h-7 w-7 rounded-lg',
        kind === 'own_domain' ? 'bg-accent/10 text-accent' : 'bg-info/15 text-info',
      )}
    >
      <Icon className={size === 'md' ? 'h-[18px] w-[18px]' : 'h-4 w-4'} aria-hidden="true" />
    </span>
  )
}

export function IdentityList({
  account,
  onAdd,
  onEdit,
}: {
  account: { id: string; label: string; disabledAt: string | null }
  onAdd: () => void
  onEdit: (identity: PublicMailIdentity) => void
}) {
  const { data, isPending, error } = useQuery({
    queryKey: identitiesQueryKey(account.id),
    queryFn: () => api.listMailIdentities(account.id),
  })
  const identities = data?.identities ?? []
  return (
    <section
      aria-label={`Absender von ${account.label}`}
      className="-mx-4 border-t border-border-subtle"
    >
      <div className="flex items-baseline justify-between gap-3 px-4 pb-1 pt-3">
        <h3 className="text-sm font-semibold">Absender</h3>
        <span className="text-xs text-text-tertiary">Wer steht bei „Von“?</span>
      </div>
      {isPending && (
        <p className="px-4 py-2 text-xs text-text-tertiary">Absender werden geladen …</p>
      )}
      {error && (
        <p role="alert" className="px-4 py-2 text-xs text-danger">
          Absender konnten nicht geladen werden.
        </p>
      )}
      <ul className="divide-y divide-border-subtle">
        {identities.map((identity) => {
          const badge = identityStatusBadge(identity)
          const subtitle = identitySubtitle(identity)
          const disabled = identity.status === 'disabled'
          return (
            <li key={identity.id}>
              <button
                type="button"
                onClick={() => onEdit(identity)}
                className="flex min-h-14 w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-bg-overlay"
              >
                <span className={cn(disabled && 'opacity-50')}>
                  <IdentityKindIcon kind={identity.kind} />
                </span>
                <span className={cn('min-w-0 flex-1', disabled && 'opacity-60')}>
                  <span className="block truncate text-sm font-medium text-text-primary">
                    {identity.address}
                  </span>
                  <span className="flex min-w-0 items-center gap-1.5 text-xs text-text-tertiary">
                    {subtitle && <span className="truncate">{subtitle}</span>}
                    {identity.isDefault && (
                      <span className="shrink-0 rounded-full border border-border-default px-1.5 py-px text-[11px] text-text-secondary">
                        Standard
                      </span>
                    )}
                  </span>
                </span>
                <span
                  className={cn(
                    'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
                    BADGE_TONES[badge.tone],
                  )}
                >
                  {badge.ok && <Check className="h-3 w-3" aria-hidden="true" />}
                  {badge.label}
                </span>
              </button>
            </li>
          )
        })}
      </ul>
      <div className="border-t border-border-subtle">
        <button
          type="button"
          onClick={onAdd}
          disabled={!!account.disabledAt}
          className="flex min-h-12 w-full items-center gap-2 px-4 text-sm font-medium text-accent transition-colors hover:bg-accent/5 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          Absender hinzufügen
        </button>
      </div>
      <p className="flex gap-2 border-t border-border-subtle px-4 pb-1 pt-3 text-xs text-text-tertiary">
        <Info className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span>
          <strong className="font-medium text-text-secondary">Eigene Domain</strong> geht über
          Deinen Server.{' '}
          <strong className="font-medium text-text-secondary">Fremder Postausgang</strong> sendet
          über den SMTP-Server des Anbieters – Zugang wird verschlüsselt gespeichert.
        </span>
      </p>
    </section>
  )
}
